import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, ArrowUpRight, Calendar, Check, Clock, Link2, List } from "lucide-react"
import { useBlogPost, useBlogPosts } from "@/hooks/useBlog"
import { ErrorState } from "@/components/state"
import { FacebookIcon, LinkedInIcon, TwitterIcon } from "@/components/social-icons"
import { cn } from "@/lib/utils"
import { pageMeta, syncMetaTags } from "@/lib/seo"
import { JsonLd } from "@/components/json-ld"

function titleCase(slug: string) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

export const Route = createFileRoute("/_public/blog/$slug")({
  component: BlogDetail,
  // The real title/description/cover image aren't known yet — this route
  // fetches the post client-side (useBlogPost), not through a loader, so
  // head() only ever sees the slug param. This renders a reasonable guess
  // immediately; syncMetaTags() in the component corrects it in place once
  // the actual post loads.
  head: ({ params }) =>
    pageMeta({
      title: `${titleCase(params.slug)} — Nexovia IT Blog`,
      description: "Read this article on the Nexovia IT blog.",
      path: `/blog/${params.slug}`,
    }),
})

function readingTime(html: string) {
  const words = html.replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
}

type TocItem = { id: string; text: string; level: number }

/**
 * Tags h2/h3 headings in the post's HTML with anchor ids and returns both
 * the (now id-bearing) HTML string and a flat list for the TOC. This runs
 * on the HTML *string* rather than mutating the live DOM after render,
 * because React re-inserts a dangerouslySetInnerHTML node's markup across
 * the loading -> loaded -> (TOC/related-posts data arriving) render
 * transitions, which would otherwise wipe out ids assigned post-hoc.
 */
function injectHeadingIds(html: string): { html: string; toc: TocItem[] } {
  if (!html) return { html, toc: [] }

  const doc = new DOMParser().parseFromString(html, "text/html")
  const headings = Array.from(doc.body.querySelectorAll("h2, h3")) as HTMLElement[]
  const seen = new Map<string, number>()
  const toc = headings.map((el) => {
    const base = slugify(el.textContent ?? "") || "section"
    const count = seen.get(base) ?? 0
    seen.set(base, count + 1)
    const id = count === 0 ? base : `${base}-${count}`
    el.id = id
    return { id, text: el.textContent ?? "", level: el.tagName === "H3" ? 3 : 2 }
  })

  return { html: doc.body.innerHTML, toc }
}

function TableOfContents({ toc }: { toc: TocItem[] }) {
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    if (toc.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (visible.length > 0) setActiveId(visible[0].target.id)
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 },
    )
    const elements = toc
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null)
    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [toc])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <nav aria-label="Table of contents" className="space-y-1">
      {toc.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          onClick={(e) => {
            e.preventDefault()
            scrollTo(item.id)
          }}
          className={cn(
            "block border-l-2 py-1.5 pl-3 text-sm leading-snug transition",
            item.level === 3 && "pl-6 text-xs",
            activeId === item.id
              ? "border-rust font-semibold text-ink"
              : "border-border text-muted-foreground hover:border-rust/50 hover:text-ink",
          )}
        >
          {item.text}
        </a>
      ))}
    </nav>
  )
}

function ShareButtons({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false)
  const encodedUrl = encodeURIComponent(url)
  const encodedTitle = encodeURIComponent(title)

  const links = [
    { label: "Share on LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, Icon: LinkedInIcon },
    { label: "Share on X", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, Icon: TwitterIcon },
    { label: "Share on Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, Icon: FacebookIcon },
  ]

  const iconButton = "flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:border-rust hover:text-rust"

  return (
    <div className="flex items-center gap-2">
      {links.map(({ label, href, Icon }) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} className={iconButton}>
          <Icon className="h-4 w-4" />
        </a>
      ))}
      <button
        type="button"
        aria-label="Copy link"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          } catch {
            // Clipboard API unavailable (e.g. insecure context) — silently ignore.
          }
        }}
        className={iconButton}
      >
        {copied ? <Check className="h-4 w-4 text-rust" /> : <Link2 className="h-4 w-4" />}
      </button>
    </div>
  )
}

function BlogDetail() {
  const { slug } = Route.useParams()
  const { data: post, isLoading, isError } = useBlogPost(slug)
  const { data: allPosts } = useBlogPosts()
  const { html: contentHtml, toc } = useMemo(() => injectHeadingIds(post?.content ?? ""), [post?.content])

  const morePosts = (allPosts ?? []).filter((p) => p.slug !== slug).slice(0, 3)
  const pageUrl = typeof window !== "undefined" ? window.location.href : ""

  useEffect(() => {
    if (!post) return
    syncMetaTags({
      title: `${post.title} — Nexovia IT Blog`,
      description: post.excerpt ?? undefined,
      image: post.cover_image_url ?? undefined,
    })
  }, [post])

  return (
    <>
      {post && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            ...(post.excerpt && { description: post.excerpt }),
            ...(post.cover_image_url && { image: [post.cover_image_url] }),
            ...(post.published_at && { datePublished: post.published_at }),
            dateModified: post.updated_at ?? post.published_at ?? undefined,
            ...(post.author_name && { author: { "@type": "Person", name: post.author_name } }),
            publisher: { "@type": "Organization", name: "Nexovia IT" },
            mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
          }}
        />
      )}
      <section className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-6">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-rust">
            <ArrowLeft className="h-4 w-4" /> Back to blog
          </Link>
        </div>
      </section>

      {isLoading ? (
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-24 space-y-4">
          <div className="h-4 w-1/4 animate-pulse rounded bg-secondary" />
          <div className="h-10 w-3/4 animate-pulse rounded bg-secondary" />
          <div className="aspect-[16/9] animate-pulse rounded-2xl bg-secondary" />
        </div>
      ) : isError || !post ? (
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-24">
          <ErrorState message="Couldn't load this post — it may not exist." />
        </div>
      ) : (
        <>
          {/* HERO */}
          <section className="relative overflow-hidden border-b border-border bg-background">
            <div
              className="absolute inset-0 -z-10 opacity-60"
              style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
            />
            <div className="mx-auto max-w-4xl px-6 py-16 md:py-20 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
                <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Blog
              </span>
              <h1 className="mx-auto mt-6 max-w-3xl text-4xl md:text-6xl font-bold text-balance">{post.title}</h1>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {post.published_at && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="h-4 w-4" />
                    {new Date(post.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {readingTime(post.content)} min read
                </span>
              </div>

              {post.tags.length > 0 && (
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {(post.author_name || post.author_avatar_url || pageUrl) && (
                <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
                  {(post.author_name || post.author_avatar_url) && (
                    <div className="flex items-center gap-3">
                      {post.author_avatar_url ? (
                        <img
                          src={post.author_avatar_url}
                          alt={post.author_name ?? ""}
                          width={40}
                          height={40}
                          className="h-10 w-10 rounded-full object-cover object-top"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">
                          {(post.author_name ?? "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="text-left">
                        {post.author_name && <p className="text-sm font-semibold">{post.author_name}</p>}
                        {post.author_role && <p className="text-xs text-muted-foreground">{post.author_role}</p>}
                      </div>
                    </div>
                  )}
                  {pageUrl && <ShareButtons url={pageUrl} title={post.title} />}
                </div>
              )}
            </div>
          </section>

          {post.cover_image_url && (
            <section className="mx-auto max-w-4xl px-6 pt-10 md:pt-14">
              <img
                src={post.cover_image_url}
                alt={post.title}
                width={1600}
                height={900}
                className="aspect-[16/9] w-full rounded-3xl object-cover object-top shadow-elegant"
              />
            </section>
          )}

          {/* CONTENT */}
          <section className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_240px] lg:gap-12">
              <div className="mx-auto w-full max-w-3xl">
                {toc.length > 1 && (
                  <details className="mb-8 rounded-xl border border-border bg-card p-4 lg:hidden">
                    <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                      <List className="h-4 w-4 text-rust" /> Table of contents
                    </summary>
                    <div className="mt-3">
                      <TableOfContents toc={toc} />
                    </div>
                  </details>
                )}
                <div
                  className="prose prose-neutral max-w-none text-justify prose-headings:font-display prose-a:text-rust prose-lg prose-headings:scroll-mt-24"
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              </div>

              {toc.length > 1 && (
                <aside className="hidden lg:block">
                  <div className="sticky top-24">
                    <p className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-rust">
                      <List className="h-3.5 w-3.5" /> On this page
                    </p>
                    <TableOfContents toc={toc} />
                  </div>
                </aside>
              )}
            </div>
          </section>

          {/* MORE POSTS */}
          {morePosts.length > 0 && (
            <section className="border-t border-border bg-secondary/30">
              <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-widest text-rust">Keep reading</p>
                  <h2 className="mt-3 text-3xl md:text-4xl font-bold">More From the Blog</h2>
                </div>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                  {morePosts.map((p) => (
                    <Link
                      key={p.id}
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
                    >
                      <div className="aspect-[16/9] overflow-hidden bg-secondary">
                        {p.cover_image_url ? (
                          <img
                            src={p.cover_image_url}
                            alt={p.title}
                            width={1600}
                            height={900}
                            className="h-full w-full object-cover object-top transition group-hover:scale-105"
                          />
                        ) : (
                          <div className="h-full w-full bg-gradient-hero" />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="text-lg font-semibold">{p.title}</h3>
                        {p.excerpt && <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>}
                        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink group-hover:text-rust">
                          Read more <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="mx-auto max-w-5xl px-6 py-24 md:py-28">
            <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
              <p className="text-xs font-semibold uppercase tracking-widest text-rust">Enjoyed this?</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold">Let's Talk About Your Project.</h2>
              <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                Have a similar challenge in mind? We'd be happy to share how we'd approach it.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
                  Get in touch <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  )
}
